import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { getRecapDB } from "@/lib/supabaseServices";
import { Brain, Loader2, Sparkles, TrendingUp, AlertTriangle, Target, Gauge } from "lucide-react";

const scoreColor = (s) => (s >= 75 ? "#00FF41" : s >= 50 ? "#FFD700" : "#FF003C");

const ListBlock = ({ icon: Icon, title, items, color, testid }) => (
  <div className="border border-[#1A1A24] bg-[#0C0C0E] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-4 h-4" style={{ color }} />
      <h4 className="text-[11px] tracking-[0.2em] uppercase text-[#848D97]">{title}</h4>
    </div>
    <ul className="space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2 text-sm text-[#E2E8F0] leading-relaxed">
          <span style={{ color }} className="mt-1 flex-shrink-0">
            ▹
          </span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function CoachRecap() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = async () => {
    setLoading(true);
    try {
      const resData = await getRecapDB();
      if (resData.empty) {
        toast.error("Log some trades first");
        setData(null);
      } else {
        setData(resData);
      }
    } catch {
      toast.error("Recap failed, try again");
    } finally {
      setLoading(false);
    }
  };

  const r = data?.recap;
  const sc = r ? scoreColor(r.discipline_score) : "#848D97";

  return (
    <div className="space-y-8" data-testid="coach-recap">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-white leading-none">
            AI <span className="text-[#00F0FF] text-glow-cyan">COACH</span>
          </h1>
          <p className="text-[#848D97] text-sm mt-2">
            An AI review of your journal — recurring mistakes, best setups &amp; next week's focus.
          </p>
        </div>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={generate}
          disabled={loading}
          data-testid="generate-recap"
          className="flex items-center gap-2 px-6 py-3 bg-[#00F0FF] text-black font-bold text-sm uppercase tracking-wider glow-cyan disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Analyzing..." : data ? "Regenerate" : "Generate recap"}
        </motion.button>
      </div>

      {!data && !loading && (
        <div className="border border-dashed border-[#1A1A24] py-24 text-center text-[#848D97]">
          <Brain className="w-10 h-10 mx-auto mb-4 text-[#1A1A24]" />
          Hit "Generate recap" to have the AI coach review your logged trades.
        </div>
      )}

      {loading && !data && (
        <div className="flex flex-col items-center justify-center py-24 text-[#00F0FF] gap-3">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="text-xs text-[#848D97] tracking-widest uppercase">Decrypting your edge...</span>
        </div>
      )}

      {r && (
        <motion.div
          initial={{ opacity: 0, filter: "blur(8px)" }}
          animate={{ opacity: 1, filter: "blur(0px)" }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <div className="border border-[#00F0FF]/40 glow-cyan bg-[#0C0C0E] p-6 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#848D97] mb-2">Coach Verdict</div>
              <div className="font-display font-bold text-xl text-white leading-snug" data-testid="recap-headline">
                {r.headline}
              </div>
              <div className="text-xs text-[#848D97] mt-2">
                Based on {data.trade_count} trades from {data.period || "your journal"}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#848D97] mb-1 flex items-center gap-1 justify-center">
                <Gauge className="w-3 h-3" /> Discipline
              </div>
              <div
                className="font-display font-black text-6xl leading-none"
                style={{ color: sc, textShadow: `0 0 24px ${sc}` }}
                data-testid="discipline-score"
              >
                {r.discipline_score}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ListBlock icon={TrendingUp} title="Strengths" items={r.strengths} color="#00FF41" testid="recap-strengths" />
            <ListBlock icon={AlertTriangle} title="Recurring Mistakes" items={r.mistakes} color="#FF003C" testid="recap-mistakes" />
            <ListBlock icon={Target} title="Focus Next Week" items={r.focus_next_week} color="#00F0FF" testid="recap-focus" />
          </div>
        </motion.div>
      )}
    </div>
  );
}
